import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AclPermission, AclPermissionDetail, AclPermissionsRecord, AclStrategy } from '@y42/acl/models';
import { map, Observable, of, switchMap } from 'rxjs';
import { AssetAclService } from '../../acl';
import { isFeatureChildUIModel, isFeatureUIModel, Y42FeatureToAclAssetResourceType } from '../../models';
import { AssetSidebarService } from './asset-sidebar.service';

@UntilDestroy()
@Injectable()
export class AssetSidebarAclService {
  constructor(
    private readonly assetSidebarService: AssetSidebarService,
    private readonly assetAclService: AssetAclService,
  ) {}

  checkAssetPermission({
    permission,
    strategy,
  }: {
    permission: AclPermission;
    strategy?: AclStrategy;
  }): Observable<AclPermissionDetail> {
    return this.checkAssetPermissions({
      permissions: [permission],
      strategy,
    }).pipe(map((permissionsMap) => permissionsMap[permission] || { allowed: false }));
  }

  checkAssetPermissions({
    permissions,
    strategy,
  }: {
    permissions: AclPermission[];
    strategy?: AclStrategy;
  }): Observable<AclPermissionsRecord> {
    return this.assetSidebarService.asset$.pipe(
      switchMap((asset) => {
        if (!asset) {
          return of({} as AclPermissionsRecord);
        }

        // When the asset child is a table use the parent's resource type and id
        const resourceType = Y42FeatureToAclAssetResourceType[asset.featureType];

        if (!resourceType) {
          return of({} as AclPermissionsRecord);
        }

        const assetPath = isFeatureUIModel(asset) ? asset.$path : isFeatureChildUIModel(asset) ? asset.$parent : null;

        if (!assetPath) {
          return of({} as AclPermissionsRecord);
        }

        return this.assetAclService.checkAssetPermissions({
          resourceType,
          assetPath,
          permissions,
          strategy,
        });
      }),
    );
  }
}
